import { Money } from '../shared/utils/money'
import { Company, InvestorType, TermSheet } from './stores/better-entity-store'
import { startCase } from 'lodash'

export type BatchTransaction = {
  id: string | null
  allocate_management_fees: string | null
  date: string | null
  selected_capital_type: '%' | '$'
  currency: string | null
  entered_capital_amount: number | null
  selected_time_period: string | null
  transactions: Transaction[]
}

export type Invoice = {
  id: string
  status: string
  name: string
  amount: Money
  total_amount: Money
  date: string
  management_fee: Money
  other_fee: Money
  marked_as_paid: boolean
  is_call_invoice: boolean
  paid_at: string | null
  entity: {
    _cid: string
    id: string
    entity_type: string
    name: string
  }
  investor: {
    _cid: string
    id: string
    name: string
  }
  wiring_instructions: string
  notes: string
}

// Shape of a row returned by /v4/<slug>/investing/transactions, which unions
// rows from three backend queries (capital-call / distribution / fee transfers,
// loss investments, and undistributed portfolio distributions). Most fields are
// optional because only the transfers query populates them.
export type TransactionType =
  | 'capital_call'
  | 'distribution'
  | 'management_fee'
  | 'other_fee'
  | 'syndication_fee'
  | 'operating_income'
  | 'operating_expense'
  | 'recycled_funds'
  | 'interest_allocation_received'
  | 'interest_allocation_paid'
  | 'loss_investment'
  | 'portfolio_distribution'

export interface Transaction {
  id: number
  type: TransactionType | string
  date: string
  amount: string
  currency: string
  status: string
  investor_id: number
  investor_type: InvestorType
  investor_entity_type: string
  investor_name: string | null
  company_id: number | null

  // Present on capital-call / distribution / fee transfers (fetch_transactions).
  transaction_batch_id?: number | null
  commitment_id?: number
  investor_sequence?: string
  tax_investor_name?: string | null
  invoice_id?: number | null
  breakdown?: any
  sent_at?: string | null
  batch_type?: string
  investment_type?: string
  fee_amount?: string | null
  other_fee_amount?: string | null
  profit_amount?: string | null
  return_of_capital_amount?: string | null
  preferred_return_amount?: string | null
  payment_status_marked_at?: string | null
  schedule_date?: string | null
  is_ownership_transfer?: boolean
}

export type DisbursementAllocation = {
  id: string
  investor_set_disbursement_id: object
  capital: Money
  total_received: Money
  interest_payment: Money
  related_to: string
  share_price: Money
  shares_sold: number
  type: string
  date: string
  company: Company
  term_sheet: TermSheet
}

type Option = {
  disabled?: boolean
  label: string
  text?: string
  value: string
}

export type AddOn = {
  title: string
  description?: string
  price: string
  value: any
}

export type Subscription = {
  title: string
  value: string
  included?: string[]
  excluded?: string[]
  price: string
}

export type PaymentOptions = {
  subscriptions: Subscription[]
  addOns?: AddOn[]
}

// SPV exit flow (venture360/tickets#1894)

export const SPV_EXIT_TYPES = ['Acquisition', 'IPO', 'Secondary Sale', 'Liquidation', 'Other'] as const
export type SpvExitType = (typeof SPV_EXIT_TYPES)[number]

export const SPV_EXIT_TYPE_OPTIONS: Option[] = SPV_EXIT_TYPES.map((t) => ({ label: t, value: t }))

export type LossMarkingTarget = {
  company_name: string
  path: string
}

export type ExitReadiness = {
  has_distribution_or_realized_loss: boolean
  loss_marking_target: LossMarkingTarget | null
}

// SPV close flow (venture360/tickets#2098)

export type CloseReadiness = {
  has_portfolio_investment: boolean
}
